<template>
  <div class="bg-white rounded-lg border border-growth-50 p-4">
    <div class="flex justify-between items-center mb-2">
      <h4 class="text-sm font-semibold text-growth-600 uppercase tracking-wide">Derivation Rule</h4>
      <button
        v-if="!isEditing"
        @click="$emit('start-editing')"
        class="text-xs px-2 py-1 bg-growth-10 border border-growth-50 text-growth-800 rounded hover:bg-growth-tangerine/10 hover:text-growth-tangerine hover:border-growth-tangerine transition-colors"
      >
        Edit Rule
      </button>
    </div>

    <!-- View Mode -->
    <div v-if="!isEditing">
      <code class="block bg-growth-10 border border-growth-50 p-3 rounded text-sm font-mono text-growth-800">
        {{ item.rule }}
      </code>
      <div class="mt-3 flex flex-wrap gap-2">
        <span
          v-for="tag in (type === 'layer2' ? item.layer1_tags : item.layer2_flags) || []"
          :key="tag"
          @click="type === 'layer2' ? $emit('select-dimension', tag) : $emit('select-flag', tag)"
          class="px-2 py-1 bg-growth-10 border border-growth-50 text-growth-800 rounded text-sm cursor-pointer hover:bg-growth-tangerine/10 hover:text-growth-tangerine hover:border-growth-tangerine transition-colors"
        >
          {{ formatTagName(tag) }}
        </span>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else>
      <textarea
        :value="ruleEditor.expression"
        @input="$emit('update-expression', $event.target.value)"
        class="w-full p-3 border border-growth-50 rounded font-mono text-sm bg-growth-10 text-growth-800 focus:bg-white focus:ring-2 focus:ring-growth-tangerine focus:border-growth-tangerine"
        rows="3"
        placeholder="SQL boolean expression..."
      ></textarea>
      <p class="text-xs text-growth-700 mt-1">
        {{ type === 'layer2' ? 'Reference Layer 0 columns: volume_intensity, panel_acuity, care_setting, etc.' : 'Reference Layer 1 flags: is_high_volume, is_geriatric, is_high_acuity, etc.' }}
      </p>

      <!-- Available flags (for Layer 3) -->
      <div v-if="type === 'layer3'" class="mt-2">
        <p class="text-xs text-growth-700 mb-1">Click to insert:</p>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="flag in patternsData?.layer2Flags"
            :key="flag.column"
            @click="insertFlag(flag.column)"
            class="px-2 py-0.5 bg-growth-10 border border-growth-50 text-growth-600 rounded text-xs hover:bg-growth-tangerine/10 hover:text-growth-tangerine hover:border-growth-tangerine transition-colors"
          >
            {{ flag.column.toLowerCase() }}
          </button>
        </div>
      </div>

      <!-- Preview Results -->
      <div v-if="ruleEditor.preview" class="mt-3 p-3 bg-growth-10 rounded border border-growth-50">
        <div class="flex gap-6 text-sm">
          <div>
            <span class="text-growth-600">Current:</span>
            <span class="font-semibold text-growth-800 ml-1">{{ formatNumber(ruleEditor.preview.current_count) }}</span>
          </div>
          <div>
            <span class="text-growth-600">After:</span>
            <span class="font-semibold text-growth-800 ml-1">{{ formatNumber(ruleEditor.preview.new_count) }}</span>
          </div>
          <div :class="ruleEditor.preview.diff > 0 ? 'text-growth-success' : ruleEditor.preview.diff < 0 ? 'text-growth-error' : 'text-growth-600'">
            <span>{{ ruleEditor.preview.diff > 0 ? '+' : '' }}{{ formatNumber(ruleEditor.preview.diff) }}</span>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="ruleEditor.error" class="mt-2 p-2 bg-red-50 border border-growth-error text-growth-error text-sm rounded">
        {{ ruleEditor.error }}
      </div>

      <!-- Action Buttons -->
      <div class="mt-3 flex gap-2">
        <button
          @click="$emit('preview')"
          :disabled="ruleEditor.loading"
          class="px-3 py-1.5 bg-growth-10 border border-growth-50 text-growth-800 rounded text-sm hover:bg-growth-25 disabled:opacity-50 transition-colors"
        >
          {{ ruleEditor.loading ? 'Loading...' : 'Preview' }}
        </button>
        <button
          @click="$emit('save')"
          :disabled="ruleEditor.loading || !ruleEditor.preview"
          class="px-3 py-1.5 bg-growth-tangerine text-white rounded text-sm hover:bg-growth-tangerine-hover disabled:opacity-50 transition-colors"
        >
          Save & Apply
        </button>
        <button
          @click="$emit('cancel-editing')"
          class="px-3 py-1.5 text-growth-600 text-sm hover:text-growth-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RuleEditor',
  props: {
    item: {
      type: Object,
      required: true,
    },
    ruleEditor: {
      type: Object,
      required: true,
    },
    patternsData: {
      type: Object,
      default: null,
    },
    type: {
      type: String,
      required: true,
      validator: (val) => ['layer2', 'layer3'].includes(val),
    },
  },
  emits: ['start-editing', 'cancel-editing', 'preview', 'save', 'update-expression', 'select-dimension', 'select-flag'],
  computed: {
    isEditing() {
      return this.ruleEditor.editing && this.ruleEditor.tagId === this.item.tag_id
    },
  },
  methods: {
    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num?.toLocaleString() || '0'
    },
    formatTagName(name) {
      return name.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    insertFlag(column) {
      const flag = column.toLowerCase()
      const currentExpr = this.ruleEditor.expression || ''
      const newExpr = currentExpr ? `${currentExpr} AND ${flag}` : flag
      this.$emit('update-expression', newExpr)
    },
  },
}
</script>
